"use client";

import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from "react";
import { translations } from "./translations";

export type Lang = "en" | "es";

interface LanguageContextType {
  currentLang: Lang;
  setLanguage: (lang: Lang) => void;
  t: (key: string) => string | string[];
}

const LanguageContext = createContext<LanguageContextType | null>(null);

function detectInitialLang(): Lang {
  if (typeof window !== "undefined") {
    const stored = localStorage.getItem("preferred_lang");
    if (stored === "en" || stored === "es") return stored;
  }
  if (typeof navigator !== "undefined" && navigator.language) {
    if (navigator.language.startsWith("es")) return "es";
  }
  return "en";
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [currentLang, setCurrentLang] = useState<Lang>(detectInitialLang);

  useEffect(() => {
    document.documentElement.lang = currentLang;
  }, [currentLang]);

  const setLanguage = useCallback((lang: Lang) => {
    setCurrentLang(lang);
    if (typeof window !== "undefined") {
      localStorage.setItem("preferred_lang", lang);
    }
  }, []);

  const t = useCallback(
    (key: string) => {
      return translations[currentLang][key] ?? key;
    },
    [currentLang]
  );

  return (
    <LanguageContext.Provider value={{ currentLang, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage(): LanguageContextType {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return ctx;
}